// src/components/DefaultContentManager.tsx
import React, { useState, useEffect } from 'react';
import { X, Monitor, List, Film, Image, FileText, AlertCircle, Check, Clock } from 'lucide-react';
import { 
  defaultContentApi, 
  DefaultContentType, 
  DefaultContentResponse, 
  DefaultContentOptionsResponse,
  UpdateDefaultContentRequest,
  PlaylistOptionDto,
  AssetOptionDto
} from '../services/defaultContentApi';

interface DefaultContentManagerProps {
  onClose: () => void;
}

const DefaultContentManager: React.FC<DefaultContentManagerProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentConfig, setCurrentConfig] = useState<DefaultContentResponse | null>(null);
  const [contentOptions, setContentOptions] = useState<DefaultContentOptionsResponse | null>(null);
  
  // Form state
  const [selectedType, setSelectedType] = useState<DefaultContentType>(DefaultContentType.None);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load current configuration and available options in parallel
      const [config, options] = await Promise.all([
        defaultContentApi.getCurrentConfiguration(),
        defaultContentApi.getContentOptions()
      ]);

      setCurrentConfig(config);

      // Ensure arrays exist to prevent undefined access
      setContentOptions({
        playlists: options.playlists || [],
        assets: options.assets || []
      });

      
      // Set form state to current values
      setSelectedType(config.defaultContentType);
      setSelectedContentId(config.defaultContentId || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const request: UpdateDefaultContentRequest = {
        defaultContentType: selectedType,
        defaultContentId: selectedType === DefaultContentType.None ? undefined : selectedContentId || undefined
      };

      await defaultContentApi.updateConfiguration(request);

      // Reload data to show updated state
      await loadData();
      
      // Show success and close after a brief delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getAssetIcon = (fileType: string) => {
    switch (fileType?.toLowerCase()) {
      case 'video':
        return <Film className="w-4 h-4 text-blue-500" />;
      case 'image':
        return <Image className="w-4 h-4 text-green-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading default content settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center">
          <Monitor className="w-6 h-6 text-blue-500 mr-3" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Default Content Settings</h2>
            <p className="text-sm text-gray-600">Configure fallback content when no schedules are active</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content - scrollable */}
      <div className="p-6 overflow-y-auto flex-1">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Current Status */}
        {currentConfig && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Current Configuration</h3>
            {currentConfig.defaultContentType === DefaultContentType.None ? (
              <p className="text-sm text-gray-600">
                No default content configured. 
                {currentConfig.hasSystemDefault && (
                  <span> System default "{currentConfig.systemDefaultName}" will be used.</span>
                )}
              </p>
            ) : (
              <div className="flex items-center text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                <span>
                  Using {currentConfig.contentType?.toLowerCase()}: "{currentConfig.contentName}"
                  {currentConfig.itemCount && (
                    <span className="text-gray-500"> ({currentConfig.itemCount} items)</span>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Configuration Options */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Choose Default Content Type</h3>

          {/* No Default Content Option */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-start">
              <input
                type="radio"
                name="contentType"
                value={DefaultContentType.None}
                checked={selectedType === DefaultContentType.None}
                onChange={() => {
                  setSelectedType(DefaultContentType.None);
                  setSelectedContentId(null);
                }}
                className="mt-1 mr-3"
              />
              <div>
                <h4 className="font-medium text-gray-900">No Default Content</h4>
                <p className="text-sm text-gray-600">
                  Use system default when no schedules are active
                  {currentConfig?.hasSystemDefault && (
                    <span className="text-gray-500"> (Currently: "{currentConfig.systemDefaultName}")</span>
                  )}
                </p>
              </div>
            </label>
          </div>

          {/* Default Playlist Option */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-start">
              <input
                type="radio"
                name="contentType"
                value={DefaultContentType.Playlist}
                checked={selectedType === DefaultContentType.Playlist}
                onChange={() => {
                  setSelectedType(DefaultContentType.Playlist);
                  setSelectedContentId(null);
                }}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <List className="w-4 h-4 mr-2" />
                  Use Default Playlist
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Select a playlist to display when no schedules are active
                </p>

                {selectedType === DefaultContentType.Playlist && (
                  <div className="mt-3">
                    <select
                      value={selectedContentId || ''}
                      onChange={(e) => setSelectedContentId(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!contentOptions?.playlists?.length}
                    >
                      <option value="">Select a playlist...</option>
                      {contentOptions?.playlists.map((playlist: PlaylistOptionDto) => (
                        <option key={playlist.playlistId} value={playlist.playlistId}>
                          {playlist.playlistName} ({playlist.itemCount} items)
                        </option>
                      ))}
                    </select>
                    {!contentOptions?.playlists.length && (
                      <p className="text-sm text-gray-500 mt-2">No playlists available. Create a playlist first.</p>
                    )}
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Default Asset Option */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-start">
              <input
                type="radio"
                name="contentType"
                value={DefaultContentType.Asset}
                checked={selectedType === DefaultContentType.Asset}
                onChange={() => {
                  setSelectedType(DefaultContentType.Asset);
                  setSelectedContentId(null);
                }}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Film className="w-4 h-4 mr-2" />
                  Use Default Asset
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Select a single asset (image or video) to display when no schedules are active
                </p>

                {selectedType === DefaultContentType.Asset && (
                  <div className="mt-3">
                    <select
                      value={selectedContentId || ''}
                      onChange={(e) => setSelectedContentId(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!contentOptions?.assets?.length}
                    >
                      <option value="">Select an asset...</option>
                      {contentOptions?.assets.map((asset: AssetOptionDto) => (
                        <option key={asset.assetId} value={asset.assetId}>
                          {asset.assetName} ({asset.fileType}) - {asset.fileSizeFormatted}
                        </option>
                      ))}
                    </select>
                    {!contentOptions?.assets.length && (
                      <p className="text-sm text-gray-500 mt-2">No assets available. Upload an asset first.</p>
                    )}

                    {/* Asset Preview */}
                    {selectedContentId && contentOptions?.assets && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        {(() => {
                          const asset = contentOptions.assets.find((a: AssetOptionDto) => a.assetId === selectedContentId);
                          if (!asset) return null;
                          
                          return (
                            <div className="flex items-center space-x-3">
                              {getAssetIcon(asset.fileType || '')}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{asset.assetName}</p>
                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                  <span>{asset.fileType}</span>
                                  <span>{asset.fileSizeFormatted}</span>
                                  {asset.duration && (
                                    <span className="flex items-center">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {formatDuration(asset.duration)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Footer - fixed at bottom */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          disabled={saving || (selectedType !== DefaultContentType.None && !selectedContentId)}
        >
          {saving ? (
            <span className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </span>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
};

export default DefaultContentManager;